// renderer.ts — WebGL2 gaussian-splat renderer shared by Capture's live
// preview and the Viewer. One instanced unit quad per gaussian; per-gaussian
// data lives in an RGBA32F texture (layout in shaders.ts) and the only
// per-instance attribute is the depth-sorted index, so a view change costs a
// CPU counting sort plus one small buffer upload. Clouds are swapped whole
// (setCloud) or patched by id (applyDelta) as server updates stream in.

import { applyDelta as applyDeltaToCloud, emptyCloud } from './format';
import type { SplatCloud, SplatDelta } from './format';
import { covariance3D, mat4Multiply } from './math';
import type { Lens } from './lens';
import type { Mat4 } from './math';
import { FRAGMENT_SHADER, QUAD_EXTENT_SIGMA, DATA_TEX_WIDTH, TEXELS_PER_GAUSSIAN, VERTEX_SHADER } from './shaders';
import { SortScratch, sortBackToFront } from './sort';

export interface RendererCamera {
  view: Mat4; // world → GL view
  proj: Mat4;
  fx: number; // device px
  fy: number;
  // lens, when set, renders the splats blurred as the refine trainer
  // rendered this view (shaders.ts, lens.ts).
  lens?: Lens | null;
}

function compile(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader {
  const sh = gl.createShader(type);
  if (!sh) throw new Error('renderer: createShader failed');
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(sh) ?? '';
    gl.deleteShader(sh);
    throw new Error(`renderer: shader compile failed: ${log}`);
  }
  return sh;
}

export class SplatRenderer {
  readonly gl: WebGL2RenderingContext;
  private program: WebGLProgram;
  private vao: WebGLVertexArrayObject;
  private indexBuf: WebGLBuffer;
  private dataTex: WebGLTexture;
  private uniforms: Record<string, WebGLUniformLocation | null> = {};
  private cloud: SplatCloud = emptyCloud();
  private labelFilter: Set<number> | null = null;
  private visible = new Uint32Array(0);
  private visibleCount = 0;
  private scratch = new SortScratch();
  private lastSortedView: Mat4 | null = null;
  private drawCount = 0;
  private background: [number, number, number, number] = [0, 0, 0, 0];

  constructor(readonly canvas: HTMLCanvasElement) {
    const gl = canvas.getContext('webgl2', { antialias: false, premultipliedAlpha: true, alpha: true, preserveDrawingBuffer: false });
    if (!gl) throw new Error('renderer: WebGL2 is not available in this browser');
    this.gl = gl;
    const prog = gl.createProgram();
    if (!prog) throw new Error('renderer: createProgram failed');
    gl.attachShader(prog, compile(gl, gl.VERTEX_SHADER, VERTEX_SHADER));
    gl.attachShader(prog, compile(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      throw new Error(`renderer: link failed: ${gl.getProgramInfoLog(prog) ?? ''}`);
    }
    this.program = prog;
    for (const u of ['u_data', 'u_view', 'u_proj', 'u_focal', 'u_viewport', 'u_lensOn', 'u_lens', 'u_lensPhi', 'u_lensDelta']) this.uniforms[u] = gl.getUniformLocation(prog, u);

    const vao = gl.createVertexArray();
    const quadBuf = gl.createBuffer();
    const indexBuf = gl.createBuffer();
    const tex = gl.createTexture();
    if (!vao || !quadBuf || !indexBuf || !tex) throw new Error('renderer: GL object allocation failed');
    this.vao = vao;
    this.indexBuf = indexBuf;
    this.dataTex = tex;

    gl.bindVertexArray(vao);
    const e = QUAD_EXTENT_SIGMA;
    gl.bindBuffer(gl.ARRAY_BUFFER, quadBuf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-e, -e, e, -e, e, e, -e, e]), gl.STATIC_DRAW);
    const aQuad = gl.getAttribLocation(prog, 'a_quad');
    gl.enableVertexAttribArray(aQuad);
    gl.vertexAttribPointer(aQuad, 2, gl.FLOAT, false, 0, 0);

    gl.bindBuffer(gl.ARRAY_BUFFER, indexBuf);
    gl.bufferData(gl.ARRAY_BUFFER, 4, gl.DYNAMIC_DRAW);
    const aIndex = gl.getAttribLocation(prog, 'a_index');
    gl.enableVertexAttribArray(aIndex);
    gl.vertexAttribIPointer(aIndex, 1, gl.UNSIGNED_INT, 0, 0);
    gl.vertexAttribDivisor(aIndex, 1);
    gl.bindVertexArray(null);

    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    console.info('[openscan/renderer] WebGL2 initialised', { maxTexture: gl.getParameter(gl.MAX_TEXTURE_SIZE) });
  }

  get currentCloud(): SplatCloud {
    return this.cloud;
  }

  get renderedCount(): number {
    return this.drawCount;
  }

  setBackground(rgba: [number, number, number, number]): void {
    this.background = rgba;
  }

  // setCloud replaces the whole cloud and re-uploads the data texture.
  setCloud(cloud: SplatCloud): void {
    this.cloud = cloud;
    this.upload();
  }

  // applyDelta patches the current cloud by id (updates/appends/removals).
  applyDelta(delta: SplatDelta): void {
    this.cloud = applyDeltaToCloud(this.cloud, delta);
    console.debug('[openscan/renderer] delta applied', {
      updated: delta.updated.count, removed: delta.removed.length, total: this.cloud.count,
    });
    this.upload();
  }

  // setLabelFilter restricts drawing to the given labels (null/empty = all).
  setLabelFilter(labels: readonly number[] | null): void {
    this.labelFilter = labels && labels.length ? new Set(labels) : null;
    this.rebuildVisible();
  }

  private upload(): void {
    const gl = this.gl;
    const c = this.cloud;
    const texels = Math.max(1, c.count * TEXELS_PER_GAUSSIAN);
    const height = Math.max(1, Math.ceil(texels / DATA_TEX_WIDTH));
    const data = new Float32Array(DATA_TEX_WIDTH * height * 4);
    const cov = new Float32Array(6);
    for (let i = 0; i < c.count; i++) {
      const o = i * 16;
      data[o] = c.pos[i * 3];
      data[o + 1] = c.pos[i * 3 + 1];
      data[o + 2] = c.pos[i * 3 + 2];
      data[o + 3] = 1;
      covariance3D(c.logScale.subarray(i * 3, i * 3 + 3), c.rot.subarray(i * 4, i * 4 + 4), cov, 0);
      data[o + 4] = cov[0];
      data[o + 5] = cov[1];
      data[o + 6] = cov[2];
      data[o + 7] = cov[3];
      data[o + 8] = cov[4];
      data[o + 9] = cov[5];
      data[o + 12] = c.color[i * 3];
      data[o + 13] = c.color[i * 3 + 1];
      data[o + 14] = c.color[i * 3 + 2];
      data[o + 15] = c.opacity[i];
    }
    gl.bindTexture(gl.TEXTURE_2D, this.dataTex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA32F, DATA_TEX_WIDTH, height, 0, gl.RGBA, gl.FLOAT, data);
    console.debug('[openscan/renderer] uploaded cloud', { count: c.count, texHeight: height });
    this.rebuildVisible();
  }

  private rebuildVisible(): void {
    const c = this.cloud;
    if (this.visible.length < c.count) this.visible = new Uint32Array(c.count);
    let n = 0;
    for (let i = 0; i < c.count; i++) {
      if (!this.labelFilter || this.labelFilter.has(c.label[i])) this.visible[n++] = i;
    }
    this.visibleCount = n;
    this.lastSortedView = null; // force a re-sort on the next frame
  }

  private viewChanged(view: Mat4): boolean {
    const last = this.lastSortedView;
    if (!last) return true;
    // Only the depth row matters for the order.
    for (const k of [2, 6, 10, 14]) if (Math.abs(last[k] - view[k]) > 1e-5) return true;
    return false;
  }

  // render draws one frame. Sorting reruns only when the view's depth axis
  // moved or the visible set changed.
  render(cam: RendererCamera): void {
    const gl = this.gl;
    const w = this.canvas.width, h = this.canvas.height;
    gl.viewport(0, 0, w, h);
    gl.clearColor(...this.background);
    gl.clear(gl.COLOR_BUFFER_BIT);
    if (this.visibleCount === 0) {
      this.drawCount = 0;
      return;
    }
    if (this.viewChanged(cam.view)) {
      const t0 = performance.now();
      const order = sortBackToFront(cam.view, this.cloud.pos, this.visible, this.visibleCount, this.scratch);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.indexBuf);
      gl.bufferData(gl.ARRAY_BUFFER, order, gl.DYNAMIC_DRAW);
      this.lastSortedView = new Float32Array(cam.view);
      this.drawCount = order.length;
      const dt = performance.now() - t0;
      if (dt > 30) console.debug('[openscan/renderer] slow sort', { n: order.length, ms: Math.round(dt) });
    }
    gl.useProgram(this.program);
    gl.disable(gl.DEPTH_TEST);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA); // premultiplied, back-to-front
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.dataTex);
    gl.uniform1i(this.uniforms.u_data, 0);
    gl.uniformMatrix4fv(this.uniforms.u_view, false, cam.view);
    gl.uniformMatrix4fv(this.uniforms.u_proj, false, cam.proj);
    gl.uniform2f(this.uniforms.u_focal, cam.fx, cam.fy);
    gl.uniform2f(this.uniforms.u_viewport, w, h);
    const l = cam.lens;
    gl.uniform1i(this.uniforms.u_lensOn, l ? 1 : 0);
    gl.uniform2f(this.uniforms.u_lens, l?.rho ?? 0, l?.alpha ?? 0);
    gl.uniform3f(this.uniforms.u_lensPhi, l?.phi[0] ?? 0, l?.phi[1] ?? 0, l?.phi[2] ?? 0);
    gl.uniform3f(this.uniforms.u_lensDelta, l?.delta[0] ?? 0, l?.delta[1] ?? 0, l?.delta[2] ?? 0);
    gl.bindVertexArray(this.vao);
    gl.drawArraysInstanced(gl.TRIANGLE_FAN, 0, 4, this.drawCount);
    gl.bindVertexArray(null);
  }

  viewProj(cam: RendererCamera): Mat4 {
    return mat4Multiply(cam.proj, cam.view);
  }

  dispose(): void {
    const gl = this.gl;
    gl.deleteTexture(this.dataTex);
    gl.deleteBuffer(this.indexBuf);
    gl.deleteVertexArray(this.vao);
    gl.deleteProgram(this.program);
    console.info('[openscan/renderer] disposed');
  }
}
